import { useState, useCallback, useRef, useEffect } from 'react'

import { useOnClickOutside } from './useOnClickOutside'
import { useOnWindowResize } from './useOnWindowResize'
import { IconMenuOnClickEvent } from '@/Atomic/Organisms/MenuButton/types'

// Singleton closer per logical group.
// When a menu opens, call the previous closer in that group.
const currentOpenByGroup: Map<string, () => void> = new Map()

export const useIconMenu = (options?: { group?: string }) => {
  const groupKey = options?.group || '__default__'
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const myCloserRef = useRef<(() => void) | null>(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  // FIXME: Cannot access refs during render - this causes the component not to update as expected
  // Proposed solution: Initialize the ref using a function or move this logic to useEffect
  // Option 1 (Preferred): Use a lazy initializer function or useMemo
  //   const myCloserRef = useRef<(() => void) | null>(null)
  //   if (myCloserRef.current === null) {  // Use === null instead of falsy check
  //     myCloserRef.current = () => { ... }
  //   }
  // Option 2: Move initialization to useEffect (runs after render)
  //   useEffect(() => {
  //     if (!myCloserRef.current) {
  //       myCloserRef.current = () => { ... }
  //     }
  //   }, [])
  // Note: The pattern `if (ref.current == null)` is the recommended way to check for uninitialized refs
  // Define a stable closer for this instance
  // eslint-disable-next-line react-hooks/refs
  if (!myCloserRef.current) {
    myCloserRef.current = () => {
      setMenuIsOpen(false)
      // clear registry if this instance is the current one
      const current = currentOpenByGroup.get(groupKey)
      if (current === myCloserRef.current) {
        currentOpenByGroup.delete(groupKey)
      }
    }
  }

  const closeMenu = (event: IconMenuOnClickEvent) => {
    if (event) {
      // Don't close if the click is inside a nested dropdown menu or filter list
      const target = event.target as HTMLElement
      if (
        target?.closest?.('.o-dropdown-container') ||
        target?.closest?.('.filter-list-container')
      ) {
        return
      }
      event.stopPropagation()
      myCloserRef.current?.()
    }
  }

  const [menuStyle, setMenuStyle] = useState<{
    top?: number
    width?: number | string
  }>({})

  const toggleMenu = useCallback(() => {
    setMenuIsOpen((prev) => {
      const willOpen = !prev
      if (willOpen) {
        const prevCloser = currentOpenByGroup.get(groupKey)
        if (prevCloser && prevCloser !== myCloserRef.current) {
          prevCloser()
        }
        if (myCloserRef.current) {
          currentOpenByGroup.set(groupKey, myCloserRef.current)
        }
      } else {
        const current = currentOpenByGroup.get(groupKey)
        if (current === myCloserRef.current) {
          currentOpenByGroup.delete(groupKey)
        }
      }
      return willOpen
    })
  }, [groupKey])

  const openMenu = useCallback(() => {
    const prevCloser = currentOpenByGroup.get(groupKey)
    if (prevCloser && prevCloser !== myCloserRef.current) {
      prevCloser()
    }
    if (myCloserRef.current) {
      currentOpenByGroup.set(groupKey, myCloserRef.current)
    }
    setMenuIsOpen(true)
  }, [groupKey])

  const updateMenuStyle = useCallback(() => {
    setMenuStyle({
      top: 30,
      width: 'fit-content',
    })
  }, [])

  useOnWindowResize(updateMenuStyle)
  useOnClickOutside({ elementRef: buttonRef, onClick: closeMenu })

  useEffect(() => {
    return () => {
      const current = currentOpenByGroup.get(groupKey)
      if (current === myCloserRef.current) {
        currentOpenByGroup.delete(groupKey)
      }
    }
  }, [groupKey])

  return {
    buttonRef,
    menuIsOpen,
    closeMenu,
    openMenu,
    toggleMenu,
    menuStyle,
    setMenuIsOpen,
  }
}
